import React from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Box,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  RocketLaunch,
  Person,
  ReceiptLong,
  Settings,
  Home
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH, HEADER_HEIGHT } from '../../constants/layout';
import { useSidebar } from '../../contexts/SidebarContext';
import { useAuth } from '../../contexts/AuthContext';
import { hasProjectWorkspaceAccess } from '../../utils/roles';

const Sidebar = ({ mobileOpen, handleDrawerToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isOpen, closeMobileDrawer } = useSidebar();
  const { currentUser } = useAuth();

  const clientAccess = hasProjectWorkspaceAccess(currentUser);

  const menuItems = [];

  if (clientAccess) {
    menuItems.push({
      category: 'Launchpad',
      items: [
        { text: 'Project Workspace', icon: RocketLaunch, path: '/project' },
        { text: 'Invoices & Billing', icon: ReceiptLong, path: '/project?tab=invoices' }
      ]
    });
  }

  menuItems.push({
    category: 'Account',
    items: [
      { text: 'My Profile', icon: Person, path: '/profile' },
      { text: 'Settings', icon: Settings, path: '/settings' }
    ]
  });

  // Determine if sidebar is collapsed (only on desktop)
  const isCollapsed = !isMobile && !isOpen;

  // Calculate current sidebar width
  const currentWidth = isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      if (handleDrawerToggle) {
        handleDrawerToggle();
      }
      closeMobileDrawer();
    }
  };

  const drawerContent = (
    <Box sx={{ overflow: 'auto', py: 3, px: isCollapsed ? 1 : 2 }}>
      {menuItems.map((section, index) => (
        <Box key={section.category}>
          {index > 0 && <Divider sx={{ my: 2.5, borderColor: 'rgba(255, 255, 255, 0.08)' }} />}
          {/* Category label - hide when collapsed */}
          {!isCollapsed && (
            <Typography
              variant="overline"
              sx={{
                px: 2,
                py: 1,
                display: 'block',
                fontWeight: 700,
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: '0.7rem',
                letterSpacing: '1px',
                opacity: isCollapsed ? 0 : 1,
                transition: theme.transitions.create('opacity', {
                  duration: theme.transitions.duration.shorter,
                }),
              }}
            >
              {section.category}
            </Typography>
          )}
          <List sx={{ py: 0.5 }}>
            {section.items.map((item) => {
              const isActive = location.pathname === item.path;
              const IconComponent = item.icon;

              return (
                <Tooltip
                  key={item.text}
                  title={item.text}
                  placement="right"
                  disableHoverListener={!isCollapsed}
                  disableFocusListener={!isCollapsed}
                  arrow
                  enterDelay={200}
                  enterNextDelay={100}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'rgba(15, 20, 30, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        px: 1.5,
                        py: 1,
                      }
                    },
                    arrow: {
                      sx: {
                        color: 'rgba(15, 20, 30, 0.95)',
                        '&::before': {
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        }
                      }
                    }
                  }}
                >
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      py: 1.2,
                      px: isCollapsed ? 1 : 2,
                      mb: 0.5,
                      borderRadius: '10px',
                      backgroundColor: isActive ? 'rgba(0, 122, 255, 0.15)' : 'transparent',
                      transition: 'all 0.2s ease',
                      justifyContent: isCollapsed ? 'center' : 'flex-start',
                      '&:hover': {
                        backgroundColor: isActive ? 'rgba(0, 122, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                        transform: isCollapsed ? 'scale(1.05)' : 'translateX(4px)'
                      }
                    }}
                  >
                    <ListItemIcon sx={{
                      minWidth: isCollapsed ? 'auto' : 36,
                      color: isActive ? '#007aff' : 'rgba(255, 255, 255, 0.6)',
                      justifyContent: 'center',
                    }}>
                      <IconComponent />
                    </ListItemIcon>
                    {/* Text - hide when collapsed */}
                    {!isCollapsed && (
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: {
                            fontWeight: isActive ? 600 : 500,
                            color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.8)',
                            fontSize: '0.9rem',
                            opacity: isCollapsed ? 0 : 1,
                            transition: theme.transitions.create('opacity', {
                              duration: theme.transitions.duration.shorter,
                            }),
                          }
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box component="nav">
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: 'rgba(15, 20, 30, 0.98)',
            backdropFilter: 'blur(10px)',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)',
            top: HEADER_HEIGHT,
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: currentWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: currentWidth,
            boxSizing: 'border-box',
            backgroundColor: 'rgba(15, 20, 30, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)',
            top: HEADER_HEIGHT,
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
            position: 'fixed',
            left: 0,
            zIndex: 1100,
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
